import { CSSProperties, PropsWithChildren, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/atoms/Text";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import NumberInput, { NumberInputProps } from "@/components/atoms/NumberInput";
import PhotoInput from "@/components/molecules/PhotoInput";
import { NumberValue } from "../useInspectionForm";
import { getNumberSeparators } from "../utils";
import styles from "../styles.module.css";

// Small pieces shared by the steps and their tables.

/** A form label with an optional red asterisk, for fields that aren't Mantine inputs (photos). */
export const FieldLabel = ({ required, children }: PropsWithChildren<{ required?: boolean }>) => (
  <Text size="sm" color="secondary">
    {children}
    {required && <span className={styles.required}> *</span>}
  </Text>
);

/** A table header cell: mono, uppercase. */
export const HeaderText = ({ children, className }: PropsWithChildren<{ className?: string }>) => (
  <Text display="technical" size="xs" strong tt="uppercase" className={className}>
    {children}
  </Text>
);

interface PhotoLineProps {
  photos: File[];
  onChange: (photos: File[]) => void;
  max?: number;
  error?: string;
  "data-testid"?: string;
}

/** "BILDER *" and the photo tiles, under a table row. */
export const PhotoLine = ({ photos, onChange, max, error, "data-testid": testId }: PhotoLineProps) => {
  const { t } = useTranslation();
  return (
    <div className={styles.photoLine}>
      <Text display="technical" size="xxs" strong tt="uppercase" className={styles.photoLabel}>
        {t("inspections:photos.label")}
        <span className={styles.required}> *</span>
      </Text>
      <PhotoInput value={photos} onChange={onChange} max={max} error={error} data-testid={testId} />
    </div>
  );
};

interface AddRowButtonProps {
  onClick: () => void;
  disabled?: boolean;
  "data-testid"?: string;
}

/** The "+ Add …" button under a table. */
export const AddRowButton = ({ onClick, disabled, children, ...props }: PropsWithChildren<AddRowButtonProps>) => (
  <button type="button" className={styles.addRowButton} onClick={onClick} disabled={disabled} {...props}>
    <SVGAsset name="plus-circle" width={14} height={14} alt="" />
    <Text span display="technical" size="xs" tt="uppercase">
      {children}
    </Text>
  </button>
);

/** The trash button at the end of a table row. */
export const RemoveRowButton = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <button type="button" className={styles.iconButton} onClick={onClick} aria-label={label}>
    <SVGAsset name="trashcan" width={14} height={14} alt="" />
  </button>
);

interface SummaryStripProps {
  items: { label: string; value: ReactNode }[];
}

/** The ruled row of label/value cells at the top of each step. */
export const SummaryStrip = ({ items }: SummaryStripProps) => (
  <div className={styles.summaryStrip} style={{ "--summary-columns": items.length } as CSSProperties}>
    {items.map(({ label, value }) => (
      <div key={label} className={styles.summaryCell}>
        <Text display="technical" size="xxs" strong tt="uppercase">
          {label}
        </Text>
        <Text size="sm">{value}</Text>
      </div>
    ))}
  </div>
);

interface UnitNumberInputProps extends Omit<NumberInputProps, "value" | "onChange"> {
  value: NumberValue;
  onChange: (value: NumberValue) => void;
  /** Shown inside the field, after the number (KG, T, STK …). */
  unit: string;
}

/** A non-negative number field with its unit inside, accepting "1 000" and "1,5" as typed in Norwegian. */
export const UnitNumberInput = ({ value, onChange, unit, ...props }: UnitNumberInputProps) => {
  const { i18n } = useTranslation();

  return (
    <NumberInput
      {...props}
      {...getNumberSeparators(i18n.language)}
      value={value}
      onChange={onChange}
      allowNegative={false}
      decimalScale={3}
      rightSection={unit ? <span className={styles.unit}>{unit}</span> : null}
      rightSectionWidth="auto"
      rightSectionPointerEvents="none"
    />
  );
};
